import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { ChevronLeft, CheckCircle2, Undo2, AlertTriangle } from "lucide-react";

const STATUSES = [
  { key: "pending",           label: "Pending" },
  { key: "confirmed",         label: "Confirmed" },
  { key: "paid",              label: "Paid" },
  { key: "fitting_scheduled", label: "Fitting Scheduled" },
  { key: "completed",         label: "Completed" },
  { key: "refunded",          label: "Refunded" },
  { key: "cancelled",         label: "Cancelled" },
];

const AdminOrderDetail = () => {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Refund state
  const [payment, setPayment] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [refundMsg, setRefundMsg] = useState("");
  const [refundErr, setRefundErr] = useState("");

  const load = async () => {
    const [orderRes, payRes] = await Promise.all([
      adminApi.get(`/orders/${id}`),
      adminApi.get(`/orders/${id}/payment`).catch(() => ({ data: null })),
    ]);
    setO(orderRes.data); setNewStatus(orderRes.data.status); setPayment(payRes.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const updateStatus = async () => {
    setSaving(true); setMsg(""); setErr("");
    try {
      const { data } = await adminApi.patch(`/orders/${id}/status`, { status: newStatus, note });
      setO(data); setNote(""); setMsg(`Status updated to “${newStatus}”.`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitRefund = async () => {
    setRefundMsg(""); setRefundErr("");
    const amt = refundAmount && Number(refundAmount) > 0 ? Number(refundAmount) : null;
    if (amt !== null && (amt <= 0 || amt > (payment?.amount_refundable || 0))) {
      setRefundErr(`Amount must be between £0.01 and £${(payment?.amount_refundable || 0).toFixed(2)}`);
      return;
    }
    if (!window.confirm(`Refund £${(amt ?? payment?.amount_refundable ?? 0).toFixed(2)} to the customer? This will charge it back to their card via Stripe.`)) return;
    setRefunding(true);
    try {
      const body = { note: refundNote };
      if (amt !== null) body.amount = amt;
      if (refundReason) body.reason = refundReason;
      const { data } = await adminApi.post(`/orders/${id}/refund`, body);
      setRefundMsg(`Refunded £${data.amount_refunded.toFixed(2)} via Stripe · status: ${data.payment_status}.`);
      setRefundAmount(""); setRefundReason(""); setRefundNote(""); setRefundOpen(false);
      await load();
    } catch (e) { setRefundErr(formatApiError(e)); }
    finally { setRefunding(false); }
  };

  if (!o) return <p className="vd-muted">Loading…</p>;
  const currentLabel = STATUSES.find((s) => s.key === o.status)?.label || o.status;
  const isCancelled = o.status === "cancelled";

  return (
    <div data-testid="admin-order-detail-page">
      <Link to="/admin/orders" className="inline-flex items-center gap-1 vd-muted text-sm mb-3"><ChevronLeft size={14} /> Back to orders</Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <h1 style={{ fontSize: 28 }}>Order #{o.id.slice(0, 8)}</h1>
          <p className="vd-muted">Placed {new Date(o.created_at).toLocaleString()}</p>
        </div>
        <span
          className="text-xs font-bold rounded-full px-3 py-1"
          style={{
            background: isCancelled ? "rgba(230,0,0,0.15)" : "rgba(0,180,80,0.15)",
            color: isCancelled ? "var(--primary-red)" : "#00A35C",
          }}
          data-testid="admin-order-status-badge"
        >{currentLabel}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="lg:col-span-2 flex flex-col gap-5">
          <div className="vd-card p-5" data-testid="order-items-card">
            <p className="vd-heading font-semibold mb-3">Tyres purchased</p>
            <table className="w-full text-sm">
              <thead><tr className="text-left vd-muted uppercase text-xs tracking-wider">
                <th className="py-2">Item</th><th>Qty</th><th>Price</th><th className="text-right">Line</th>
              </tr></thead>
              <tbody>
                {o.items.map((i, idx) => (
                  <tr key={idx} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`order-item-row-${idx}`}>
                    <td className="py-3 vd-heading">{i.name}</td>
                    <td className="vd-muted">{i.quantity}</td>
                    <td className="vd-muted">£{i.price.toFixed(2)}</td>
                    <td className="text-right vd-heading font-semibold">£{(i.price * i.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t mt-3 pt-3 flex justify-between" style={{ borderColor: "var(--border-color)" }}>
              <div className="vd-muted text-sm">
                <p>Subtotal: £{o.subtotal.toFixed(2)}</p>
                <p>Fitting fee: £{o.fitting_fee.toFixed(2)}</p>
              </div>
              <p className="vd-heading font-extrabold" style={{ fontSize: 22, color: "var(--primary-red)" }} data-testid="order-total">£{o.total.toFixed(2)}</p>
            </div>
          </div>

          <div className="vd-card p-5" data-testid="admin-order-status-history">
            <p className="vd-heading font-semibold mb-3">Status history</p>
            {(!o.status_history || o.status_history.length === 0) ? (
              <p className="vd-muted text-sm">No history yet.</p>
            ) : (
              <ul className="space-y-3">
                {[...o.status_history].reverse().map((h, i) => (
                  <li key={i} className="flex gap-3" data-testid={`admin-order-history-${i}`}>
                    <span className="inline-flex shrink-0 items-center justify-center rounded-full" style={{ width: 28, height: 28, background: i === 0 ? "var(--primary-red)" : "var(--bg-secondary)", border: "1px solid var(--border-color)" }}>
                      <CheckCircle2 size={14} color={i === 0 ? "#fff" : "var(--text-muted)"} />
                    </span>
                    <div className="flex-1 border-b pb-3" style={{ borderColor: "var(--border-color)" }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="vd-heading font-semibold text-sm">{STATUSES.find((s) => s.key === h.status)?.label || h.status}</p>
                        {h.admin_user && <span className="vd-muted text-xs">by {h.admin_user}</span>}
                      </div>
                      {h.note && <p className="vd-muted text-sm">{h.note}</p>}
                      <p className="vd-muted text-xs mt-0.5">{new Date(h.timestamp).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div className="vd-card p-5" data-testid="order-customer-card">
            <p className="vd-heading font-semibold mb-3">Customer</p>
            <DetailRow k="Name" v={o.full_name} />
            <DetailRow k="Phone" v={o.phone} />
            <DetailRow k="Address" v={`${o.address}, ${o.postcode}`} />
            {(o.service_type || "mobile_fitting") === "delivery"
              ? <DetailRow k="Fulfilment" v="Delivery only (no fitting)" />
              : <DetailRow k="Fitting" v={`${o.fitting_date || "—"} · ${o.time_slot || "—"}`} />}
            <DetailRow k="Payment" v={o.payment_method} />
          </div>

          <div className="vd-card p-5" data-testid="admin-status-editor">
            <p className="vd-heading font-semibold mb-3">Update status</p>
            <label className="vd-muted text-xs uppercase tracking-wider">New status</label>
            <select className="vd-select mt-1" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="admin-status-select">
              {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <label className="vd-muted text-xs uppercase tracking-wider mt-3 inline-block">Note (optional)</label>
            <input className="vd-input mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Fitter assigned to your area" data-testid="admin-status-note" />
            <button
              onClick={updateStatus}
              disabled={saving || newStatus === o.status}
              className="vd-btn-primary w-full justify-center mt-4"
              data-testid="admin-status-save"
            >{saving ? "Saving…" : "Save status"}</button>
            {msg && <p className="text-xs mt-2" style={{ color: "#00A35C" }} data-testid="admin-status-msg">{msg}</p>}
            {err && <p className="text-xs mt-2" style={{ color: "var(--primary-red)" }} data-testid="admin-status-err">{err}</p>}
            <p className="vd-muted text-xs mt-3">The customer can view all status changes on the public Track Order page.</p>
          </div>

          {payment && (
            <div className="vd-card p-5" data-testid="admin-refund-card">
              <p className="vd-heading font-semibold mb-3 inline-flex items-center gap-2"><Undo2 size={16} color="var(--primary-red)" /> Refund (Stripe)</p>
              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                <RefundStat k="Paid" v={`£${payment.amount_paid.toFixed(2)}`} />
                <RefundStat k="Refunded so far" v={`£${payment.amount_refunded.toFixed(2)}`} />
                <RefundStat k="Refundable" v={`£${payment.amount_refundable.toFixed(2)}`} highlight />
                <RefundStat k="Payment status" v={(payment.transaction?.payment_status || "—").replace("_", " ")} />
              </div>

              {!payment.can_refund ? (
                <div className="flex items-start gap-2 text-xs vd-muted" data-testid="refund-disabled">
                  <AlertTriangle size={14} color="#B57E00" className="mt-0.5" />
                  <span>{payment.amount_refunded > 0 ? "Fully refunded — nothing left to return." : "Refund is available only for paid orders. Once the customer's payment clears, this button activates."}</span>
                </div>
              ) : !refundOpen ? (
                <button onClick={() => setRefundOpen(true)} className="vd-btn-primary w-full justify-center" data-testid="refund-open-btn"><Undo2 size={14} /> Issue refund</button>
              ) : (
                <div className="flex flex-col gap-3">
                  <label className="vd-muted text-xs uppercase tracking-wider">Amount (£) — leave blank for full refund</label>
                  <input
                    type="number" step="0.01" min="0.01" max={payment.amount_refundable}
                    className="vd-input"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder={`Up to £${payment.amount_refundable.toFixed(2)}`}
                    data-testid="refund-amount-input"
                  />
                  <label className="vd-muted text-xs uppercase tracking-wider">Reason (optional)</label>
                  <select className="vd-select" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} data-testid="refund-reason-select">
                    <option value="">—</option>
                    <option value="requested_by_customer">Requested by customer</option>
                    <option value="duplicate">Duplicate charge</option>
                    <option value="fraudulent">Fraudulent</option>
                  </select>
                  <label className="vd-muted text-xs uppercase tracking-wider">Internal note (optional)</label>
                  <input className="vd-input" value={refundNote} onChange={(e) => setRefundNote(e.target.value)} placeholder="e.g. Wrong size delivered" data-testid="refund-note-input" />
                  {refundErr && <p className="text-xs" style={{ color: "var(--primary-red)" }} data-testid="refund-err">{refundErr}</p>}
                  <div className="flex gap-2">
                    <button onClick={submitRefund} disabled={refunding} className="vd-btn-primary justify-center flex-1" data-testid="refund-submit-btn">{refunding ? "Refunding…" : "Confirm refund"}</button>
                    <button type="button" onClick={() => { setRefundOpen(false); setRefundErr(""); }} className="vd-btn-secondary justify-center" data-testid="refund-cancel-btn">Cancel</button>
                  </div>
                </div>
              )}

              {refundMsg && <p className="text-xs mt-3" style={{ color: "#00A35C" }} data-testid="refund-msg">{refundMsg}</p>}

              {payment.transaction?.refunds?.length > 0 && (
                <div className="mt-4 border-t pt-3" style={{ borderColor: "var(--border-color)" }} data-testid="refund-history">
                  <p className="vd-muted text-xs uppercase tracking-wider mb-2">Refund history</p>
                  <ul className="text-xs space-y-2">
                    {payment.transaction.refunds.map((r) => (
                      <li key={r.id} className="flex items-center justify-between gap-2" data-testid={`refund-row-${r.id}`}>
                        <span className="vd-heading font-semibold">£{r.amount.toFixed(2)}</span>
                        <span className="vd-muted">{r.status} · {r.reason || "no reason"}</span>
                        <span className="vd-muted">{new Date(r.created_at).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RefundStat = ({ k, v, highlight }) => (
  <div className="rounded-md p-2" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)" }}>
    <p className="vd-muted text-[10px] uppercase tracking-wider">{k}</p>
    <p className="vd-heading font-bold" style={{ fontSize: 14, color: highlight ? "var(--primary-red)" : "var(--text-heading)" }}>{v}</p>
  </div>
);

const DetailRow = ({ k, v }) => (
  <div className="py-1.5 border-b last:border-b-0" style={{ borderColor: "var(--border-color)" }}>
    <p className="vd-muted text-xs uppercase tracking-wider">{k}</p>
    <p className="vd-heading font-medium text-sm">{v || "—"}</p>
  </div>
);

export default AdminOrderDetail;
